import React from 'react';
import { ArrowRight, MapPin } from 'lucide-react';
import useGameStore from '../../game/gameStore';
import { capitalize, PARTY_COLORS } from './constants';

const PLAYER_COLOR = '#4ade80';

function getPartyColor(partido) {
  return PARTY_COLORS[partido] || '#888';
}

function verLabel(ver, playerVereadorId) {
  if (!ver) return { name: 'Ninguém', color: '#556688' };
  if (ver.id === playerVereadorId) return { name: 'Você', color: PLAYER_COLOR };
  return { name: capitalize(ver.nome).split(' ').slice(0, 2).join(' '), color: getPartyColor(ver.partido) };
}

function ChangeRow({ change, vereadorMap, playerVereadorId }) {
  const from = change.fromId ? vereadorMap[change.fromId] : null;
  const to = change.toId ? vereadorMap[change.toId] : null;
  const fromLabel = verLabel(from, playerVereadorId);
  const toLabel = verLabel(to, playerVereadorId);

  const isPlayerGain = change.toId === playerVereadorId;
  const isPlayerLoss = change.fromId === playerVereadorId && change.toId !== playerVereadorId;

  let message;
  if (to && from) {
    message = <>
      <span style={{ color: toLabel.color, fontWeight: 700 }}>{toLabel.name}</span> conquistou de{' '}
      <span style={{ color: fromLabel.color, fontWeight: 700 }}>{fromLabel.name}</span>
    </>;
  } else if (to && !from) {
    message = <>
      <span style={{ color: toLabel.color, fontWeight: 700 }}>{toLabel.name}</span> conquistou{' '}
      <span className="text-gray-500">(era neutro)</span>
    </>;
  } else {
    message = <>
      <span style={{ color: fromLabel.color, fontWeight: 700 }}>{fromLabel.name}</span> perdeu o controle{' '}
      <span className="text-gray-500">(ficou neutro)</span>
    </>;
  }

  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{
      background: isPlayerGain ? 'rgba(74,222,128,0.08)' : isPlayerLoss ? 'rgba(239,68,68,0.08)' : '#0f1a30',
      border: `1px solid ${isPlayerGain ? 'rgba(74,222,128,0.25)' : isPlayerLoss ? 'rgba(239,68,68,0.25)' : '#1e3050'}`,
    }}>
      <MapPin size={14} className="shrink-0" style={{ color: isPlayerGain ? PLAYER_COLOR : isPlayerLoss ? '#ef4444' : '#6a7fa0' }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-white truncate">{change.territoryName}</p>
        <p className="text-[10px] text-gray-400">{message}</p>
      </div>
      {isPlayerGain && <span className="text-[9px] font-bold shrink-0" style={{ color: PLAYER_COLOR }}>+VOCÊ</span>}
      {isPlayerLoss && <span className="text-[9px] font-bold shrink-0 text-red-400">-VOCÊ</span>}
    </div>
  );
}

export default function TurnSummaryOverlay() {
  const turnState = useGameStore(s => s.turnState);
  const turn = useGameStore(s => s.turn);
  const maxTurns = useGameStore(s => s.maxTurns);
  const lastTerritoryChanges = useGameStore(s => s.lastTerritoryChanges);
  const vereadores = useGameStore(s => s.vereadores);
  const playerVereadorId = useGameStore(s => s.playerVereadorId);
  const dismissTurnSummary = useGameStore(s => s.dismissTurnSummary);

  if (turnState !== 'turnSummary') return null;

  const vereadorMap = {};
  for (const v of vereadores) vereadorMap[v.id] = v;

  const changes = lastTerritoryChanges || [];
  const gains = changes.filter(c => c.toId === playerVereadorId).length;
  const losses = changes.filter(c => c.fromId === playerVereadorId && c.toId !== playerVereadorId).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ background: 'rgba(2,8,16,0.82)' }}>
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden animate-[slideUp_0.3s_ease-out] sm:animate-[fadeInScale_0.3s_ease-out] max-h-[90vh] flex flex-col"
        style={{ background: '#0b1424', border: '1px solid #182440' }}>
        <div className="h-1 w-full shrink-0" style={{ background: '#d4a574' }} />

        <div className="p-4 sm:p-6 overflow-y-auto flex-1" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base sm:text-lg font-bold text-white">Resumo do Turno {turn}/{maxTurns}</h3>
          </div>
          <p className="text-xs text-gray-500 mb-4">Mudanças de controle territorial nesta rodada</p>

          {changes.length > 0 && (
            <div className="flex items-center gap-3 mb-4 text-[10px] font-bold">
              {gains > 0 && <span className="px-2 py-1 rounded" style={{ background: 'rgba(74,222,128,0.1)', color: PLAYER_COLOR }}>+{gains} conquistado{gains > 1 ? 's' : ''}</span>}
              {losses > 0 && <span className="px-2 py-1 rounded bg-red-500/10 text-red-400">-{losses} perdido{losses > 1 ? 's' : ''}</span>}
            </div>
          )}

          {changes.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">Nenhuma mudança de controle territorial neste turno.</p>
              <p className="text-[11px] text-gray-600 mt-1">Os territórios seguem com os mesmos donos de antes.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {changes.map((c) => (
                <ChangeRow key={c.territoryId} change={c} vereadorMap={vereadorMap} playerVereadorId={playerVereadorId} />
              ))}
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-white/[.06]">
          <button
            onClick={dismissTurnSummary}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all"
            style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}
          >
            Continuar <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
